import {
  FIT_ANIMATION_MS,
  REDUCED_MOTION_CLASS,
  VIEW_TOLERANCE,
  ZOOM_MAX,
  ZOOM_MIN,
  ariaKeyShortcutsFromLabel,
  canZoomIn,
  canZoomOut,
  defaultMessages,
  easeStandard,
  fitViewbox,
  freeRectCenter,
  interpolateViewbox,
  isTextField,
  isUndoRedoKey,
  matchChromeKey,
  sameView
} from "./chunk-OJ7T5HEP.js";

// src/diagram-js/palette-skin.ts
var TOOLS_GROUP = "tools";
var READ_ONLY_ACTIONS = /* @__PURE__ */ new Set(["lasso-tool", "hand-tool"]);
var TOOL_ACTION_SUFFIX = "-tool";
var ENTRIES_SELECTOR = ".djs-palette-entries";
var ENTRY_SELECTOR = ".entry";
var PaletteSkin = class {
  constructor(element, readEntries) {
    this.element = element;
    this.readEntries = readEntries;
    element.setAttribute("data-chrome", "palette");
    element.addEventListener("keydown", this.onKeydown);
    element.addEventListener("focusin", this.onFocusIn);
    element.addEventListener("focusout", this.onFocusOut);
    this.refresh();
  }
  element;
  readEntries;
  label = defaultMessages.tools;
  activeTool = null;
  /** The veto of a read-only canvas, at once with the invalid import. */
  locked = false;
  /** The `aria-disabled` marks of a read-only canvas, once the shell's delay has passed. */
  readOnlyShown = false;
  entries = {};
  rovingAction;
  focusedEntry;
  /** The `aria-disabled` an entry had before read-only marked it (`null`: none). */
  disabledEntries = /* @__PURE__ */ new WeakMap();
  setLabel(label) {
    this.label = label;
    this.apply();
  }
  setActiveTool(tool) {
    this.activeTool = tool;
    this.apply();
  }
  setReadOnly(state) {
    this.locked = state.locked;
    this.readOnlyShown = state.locked && state.shown;
    this.apply();
  }
  /**
   * Whether `palette.trigger` must refuse `entry`: on a locked canvas every entry but Lasso and
   * Hand, also before their marks show. `palette.trigger` carries the entry definition without its
   * id, so the entry element is found through the DOM event; without one, only the tools group
   * passes.
   */
  vetoes(entry, event) {
    if (!this.locked) return false;
    const element = this.entryOf(
      event?.delegateTarget ?? event?.target ?? null
    );
    if (element) return !staysActive(element);
    return entry.group !== TOOLS_GROUP;
  }
  /** After `palette.changed`: re-read the entries and re-apply every attribute. */
  refresh() {
    this.entries = this.readEntries();
    this.apply();
  }
  entryTooltip(entry) {
    const action = entry.getAttribute("data-action");
    const definition = action === null ? void 0 : this.entries[action];
    if (!definition?.title) return null;
    return definition.shortcut ? { label: definition.title, shortcut: definition.shortcut } : { label: definition.title };
  }
  destroy() {
    this.element.removeEventListener("keydown", this.onKeydown);
    this.element.removeEventListener("focusin", this.onFocusIn);
    this.element.removeEventListener("focusout", this.onFocusOut);
    this.focusedEntry = void 0;
  }
  apply() {
    const container = this.element.querySelector(ENTRIES_SELECTOR);
    if (container) {
      container.setAttribute("role", "toolbar");
      container.setAttribute("aria-label", this.label);
    }
    const entries = this.entryElements();
    for (const entry of entries) {
      if (!entry.hasAttribute("role")) entry.setAttribute("role", "button");
      this.applyKeyShortcuts(entry);
      this.applyPressed(entry);
      this.applyDisabled(entry);
    }
    const stop = this.applyTabStops(entries);
    this.restoreFocus(entries, stop);
  }
  // Guide §5.6: a button with a shortcut names it for assistive technology, not only in the chip.
  applyKeyShortcuts(entry) {
    const action = actionOf(entry);
    const definition = action === void 0 ? void 0 : this.entries[action];
    const keyShortcuts = definition?.ariaKeyShortcuts ?? (definition?.shortcut ? ariaKeyShortcutsFromLabel(definition.shortcut) : void 0);
    if (keyShortcuts) entry.setAttribute("aria-keyshortcuts", keyShortcuts);
  }
  applyPressed(entry) {
    const action = actionOf(entry);
    if (groupOf(entry) !== TOOLS_GROUP || !action?.endsWith(TOOL_ACTION_SUFFIX)) return;
    const tool = action.slice(0, -TOOL_ACTION_SUFFIX.length);
    entry.setAttribute("aria-pressed", String(tool === this.activeTool));
  }
  applyDisabled(entry) {
    if (this.readOnlyShown && !staysActive(entry)) {
      if (!this.disabledEntries.has(entry)) {
        this.disabledEntries.set(entry, entry.getAttribute("aria-disabled"));
      }
      entry.setAttribute("aria-disabled", "true");
      return;
    }
    if (!this.disabledEntries.has(entry)) return;
    const previous = this.disabledEntries.get(entry);
    this.disabledEntries.delete(entry);
    if (previous === null || previous === void 0) entry.removeAttribute("aria-disabled");
    else entry.setAttribute("aria-disabled", previous);
  }
  applyTabStops(entries) {
    const stop = entries.find((entry) => actionOf(entry) === this.rovingAction) ?? entries[0];
    for (const entry of entries) entry.tabIndex = entry === stop ? 0 : -1;
    return stop;
  }
  // A rebuild removes the focused entry; without this the focus would fall back to <body>. An
  // entry the rebuilt palette no longer offers (another level, another mode) hands the focus to the
  // tab stop, so the keyboard user stays in the toolbar.
  restoreFocus(entries, stop) {
    const lost = this.focusedEntry;
    if (!lost || lost.isConnected) return;
    this.focusedEntry = void 0;
    const replacement = entries.find((entry) => actionOf(entry) === actionOf(lost)) ?? stop;
    const active = this.element.ownerDocument.activeElement;
    const focusIsNowhere = !active || active === this.element.ownerDocument.body;
    if (replacement && focusIsNowhere) replacement.focus();
  }
  entryElements() {
    const container = this.element.querySelector(ENTRIES_SELECTOR);
    return container ? Array.from(container.querySelectorAll(ENTRY_SELECTOR)) : [];
  }
  entryOf(target) {
    if (!(target instanceof Element)) return void 0;
    const entry = target.closest(ENTRY_SELECTOR);
    return entry && this.element.contains(entry) ? entry : void 0;
  }
  onFocusIn = (event) => {
    const entry = this.entryOf(event.target);
    if (!entry) return;
    this.focusedEntry = entry;
    this.rovingAction = actionOf(entry);
    this.applyTabStops(this.entryElements());
  };
  // A focus that leaves the palette is not brought back by a later rebuild. Chromium reports the
  // removal of the focused entry as a focusout too, while the entry is still in the document, and
  // the rebuild's `palette.changed` follows in the same task: so the entry counts as left only once
  // that task is over and the focus has not come back to it.
  onFocusOut = (event) => {
    const entry = this.entryOf(event.target);
    if (!entry || entry !== this.focusedEntry) return;
    const next = event.relatedTarget;
    if (next instanceof Node && this.element.contains(next)) return;
    queueMicrotask(() => {
      if (this.focusedEntry === entry && entry.ownerDocument.activeElement !== entry) {
        this.focusedEntry = void 0;
      }
    });
  };
  onKeydown = (event) => {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey)
      return;
    const entry = this.entryOf(event.target);
    if (!entry || entry.closest("[inert]") || !isRendered(entry)) return;
    const entries = this.entryElements();
    const index = entries.indexOf(entry);
    const last = entries.length - 1;
    let next;
    switch (event.key) {
      case "ArrowLeft":
        next = entries[index <= 0 ? last : index - 1];
        break;
      case "ArrowRight":
        next = entries[index >= last ? 0 : index + 1];
        break;
      case "Home":
        next = entries[0];
        break;
      case "End":
        next = entries[last];
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        event.stopPropagation();
        if (!event.repeat) activate(entry);
        return;
      default:
        return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (!next) return;
    this.rovingAction = actionOf(next);
    this.applyTabStops(entries);
    next.focus();
  };
};
function isRendered(entry) {
  return typeof entry.checkVisibility !== "function" || entry.checkVisibility({ visibilityProperty: true });
}
function actionOf(entry) {
  return entry.getAttribute("data-action") ?? void 0;
}
function staysActive(entry) {
  const action = actionOf(entry);
  return action !== void 0 && READ_ONLY_ACTIONS.has(action);
}
function groupOf(entry) {
  return entry.closest(".group")?.getAttribute("data-group") ?? void 0;
}
function activate(entry) {
  const bounds = entry.getBoundingClientRect();
  entry.dispatchEvent(
    new MouseEvent("click", {
      bubbles: true,
      cancelable: true,
      clientX: bounds.left + bounds.width / 2,
      clientY: bounds.top + bounds.height / 2
    })
  );
}

// src/diagram-js/service.ts
var SHELL_SERVICE = "modelerShell";
var KEYBOARD_PRIORITY = 2e3;
var TRIGGER_PRIORITY = 2e3;
var EDITABLE = { locked: false, shown: false };
var CANVAS_ATTRIBUTES = ["role", "aria-label"];
var ShellDiagramService = class {
  constructor(eventBus, injector) {
    this.eventBus = eventBus;
    this.injector = injector;
    this.subscribe("canvas.init", (event) => {
      this.svg = event.svg;
      this.nameCanvas();
    });
    this.subscribe(
      "palette.create",
      (event) => this.onPaletteCreate(event.container)
    );
    this.subscribe("palette.changed", () => this.skin?.refresh());
    this.subscribe("tool-manager.update", (event) => {
      this.activeTool = event.tool ?? null;
      this.armedPrefix = this.activeTool === null ? void 0 : this.dragPrefix();
      this.droppedTool = void 0;
      this.skin?.setActiveTool(this.activeTool);
    });
    this.subscribe("drag.cleanup", () => this.onDragCleanup());
    this.subscribe("drag.ended", () => {
      this.droppedTool = void 0;
    });
    this.subscribe("palette.trigger", this.onPaletteTrigger, TRIGGER_PRIORITY);
    this.subscribe("diagram.destroy", () => this.destroy());
    const keyboard = injector.get("keyboard", false);
    if (keyboard) {
      keyboard.addListener(KEYBOARD_PRIORITY, this.onKeyboardKeydown);
      this.subscriptions.push(["keyboard.keydown", this.onKeyboardKeydown]);
    }
  }
  eventBus;
  injector;
  static $inject = ["eventBus", "injector"];
  svg;
  /** The canvas `<svg>`'s own `role` and `aria-label` before the shell named it. */
  canvasOriginals;
  skin;
  activeTool = null;
  /** The prefix of the drag that waits for the active tool's next press. */
  armedPrefix;
  droppedTool;
  shell;
  unregisterPalette;
  subscriptions = [];
  destroyed = false;
  /**
   * Hands the palette (now or once diagram-js creates it) to `shell`; returns the detach
   * function. A later call replaces the earlier shell.
   */
  canvasOwnsHistory = true;
  attachShell(shell, options = {}) {
    this.detachShell();
    if (this.destroyed) return () => void 0;
    this.shell = shell;
    this.canvasOwnsHistory = options.canvasOwnsHistory ?? shell.host !== "ide";
    this.skin?.setLabel(shell.messages.tools);
    this.nameCanvas();
    this.registerPalette();
    return () => {
      if (this.shell === shell) this.detachShell();
    };
  }
  registerPalette() {
    const { shell, skin } = this;
    if (!shell || !skin) return;
    this.unregisterPalette?.();
    this.unregisterPalette = shell.registerPalette({
      element: skin.element,
      entryTooltip: (entry) => skin.entryTooltip(entry),
      setReadOnly: (state) => skin.setReadOnly(state)
    });
  }
  detachShell() {
    const unregister = this.unregisterPalette;
    this.unregisterPalette = void 0;
    this.shell = void 0;
    unregister?.();
    this.unnameCanvas();
    this.skin?.setReadOnly(EDITABLE);
    this.skin?.setLabel(defaultMessages.tools);
  }
  /**
   * The focusable canvas is an application with a name (WCAG 4.1.2): screen readers switch to focus
   * mode, so the canvas's single-key commands reach it. A role or name the modeler set stays.
   */
  nameCanvas() {
    const { svg, shell } = this;
    if (!svg || !shell || this.canvasOriginals) return;
    this.canvasOriginals = new Map(CANVAS_ATTRIBUTES.map((name) => [name, svg.getAttribute(name)]));
    if (!svg.hasAttribute("role")) svg.setAttribute("role", "application");
    if (!svg.hasAttribute("aria-label") && !svg.hasAttribute("aria-labelledby")) {
      svg.setAttribute("aria-label", shell.messages.canvas);
    }
  }
  unnameCanvas() {
    const { svg, canvasOriginals } = this;
    this.canvasOriginals = void 0;
    if (!svg || !canvasOriginals) return;
    for (const [name, value] of canvasOriginals) {
      if (value === null) svg.removeAttribute(name);
      else svg.setAttribute(name, value);
    }
  }
  onPaletteCreate(container) {
    this.skin?.destroy();
    const palette = this.injector.get("palette", false);
    const skin = new PaletteSkin(container, () => palette?.getEntries() ?? {});
    this.skin = skin;
    skin.setActiveTool(this.activeTool);
    if (this.shell) skin.setLabel(this.shell.messages.tools);
    this.moveBeforeCanvas(container);
    this.registerPalette();
  }
  // Palette#_init appends the palette after the <svg>; in front of it the palette comes first in
  // the tab order (guide §5.14).
  moveBeforeCanvas(container) {
    const parent = container.parentElement ?? this.canvasContainer();
    if (!parent) return;
    const svg = this.svg?.parentNode === parent ? this.svg : Array.from(parent.children).find((child) => child.localName === "svg");
    if (svg) parent.insertBefore(container, svg);
  }
  canvasContainer() {
    const canvas = this.injector.get("canvas", false);
    return canvas?.getContainer();
  }
  dragPrefix() {
    const dragging = this.injector.get("dragging", false);
    return dragging?.context()?.prefix;
  }
  /**
   * diagram-js's tool manager puts a tool down when its drag ends, but on a cancel only for a drag
   * the pointer has moved and not from the palette's tools group: Escape on a tool the palette armed
   * before the pointer moved left it pressed (guide §5.5: pressed only while armed). The armed drag
   * also goes when another tool's drag replaces it, whose init follows in the same task, so the
   * decision waits until that task is over.
   */
  onDragCleanup() {
    const prefix = this.dragPrefix();
    const tool = this.activeTool;
    if (prefix === void 0 || prefix !== this.armedPrefix || tool === null) return;
    const dropped = { tool, prefix };
    this.droppedTool = dropped;
    queueMicrotask(() => {
      if (this.droppedTool !== dropped) return;
      this.droppedTool = void 0;
      const toolManager = this.injector.get("toolManager", false);
      if (!toolManager?.isActive(tool) || this.dragPrefix() === prefix) return;
      toolManager.setActive(null);
    });
  }
  onPaletteTrigger = (event) => {
    if (!event.entry || !this.skin?.vetoes(event.entry, event.event)) return void 0;
    event.event?.preventDefault();
    return false;
  };
  /**
   * `true` stops the diagram-js keyboard (and makes it call `preventDefault`), `false` stops it
   * without: a chrome key in a text field inside the `<svg>` stays the field's (Shift+1 types "!"),
   * and where the canvas does not own the history (the IDE host but Orca) undo and redo stay the
   * document's (guide §13.5), so they reach VS Code instead of the canvas `commandStack`.
   */
  onKeyboardKeydown = (context) => {
    const event = context.keyEvent;
    if (!event) return void 0;
    if (matchChromeKey(event))
      return !isTextField(event.target) && this.shell?.isEmptyStateShown?.() !== true;
    if (this.shell && !this.canvasOwnsHistory && isUndoRedoKey(event)) return false;
    return void 0;
  };
  subscribe(name, listener, priority) {
    const handler = listener;
    if (priority === void 0) this.eventBus.on(name, handler);
    else this.eventBus.on(name, priority, handler);
    this.subscriptions.push([name, handler]);
  }
  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    this.detachShell();
    this.skin?.destroy();
    this.skin = void 0;
    this.svg = void 0;
    this.droppedTool = void 0;
    for (const [name, listener] of this.subscriptions.splice(0)) this.eventBus.off(name, listener);
  }
};
function shellServiceOf(diagram) {
  let service;
  try {
    service = diagram.get(SHELL_SERVICE);
  } catch {
    service = void 0;
  }
  if (!isShellService(service)) {
    throw new Error(
      "@miragon/modeler-shell: connectDiagramJs needs shellModule in the modeler's modules (additionalModules: [shellModule])"
    );
  }
  return service;
}
function isShellService(value) {
  return typeof value?.attachShell === "function";
}

// src/diagram-js/module.ts
var shellModule = {
  __init__: [SHELL_SERVICE],
  [SHELL_SERVICE]: ["type", ShellDiagramService],
  paletteTooltip: ["value", null]
};

// src/diagram-js/viewport.ts
function createViewport(diagram, options) {
  return new DiagramViewport(diagram, options);
}
var IMPORT_START_EVENTS = ["import.parse.start", "import.render.start"];
var IMPORT_DONE_EVENT = "import.done";
var CONTENT_EVENTS = [
  "shape.added",
  "shape.removed",
  "connection.added",
  "connection.removed",
  "diagram.clear"
];
var ZOOM_STEP = Math.log10(ZOOM_MAX / ZOOM_MIN) / 10;
var CAPTURE = { capture: true };
var INSET_SIDES = ["top", "right", "bottom", "left"];
var DiagramViewport = class {
  constructor(diagram, options) {
    this.diagram = diagram;
    this.options = options;
    this.canvas = diagram.get("canvas");
    this.eventBus = diagram.get("eventBus");
    this.container = this.canvas.getContainer();
    this.subscribe("canvas.viewbox.changing", this.notify);
    this.subscribe("canvas.viewbox.changed", this.onViewboxChanged);
    this.subscribe(IMPORT_START_EVENTS, this.onImportStart);
    this.subscribe(IMPORT_DONE_EVENT, this.onImportDone);
    this.subscribe("elements.changed", this.onElementsChanged);
    this.subscribe(CONTENT_EVENTS, this.onContentChanged);
    this.subscribe("diagram.destroy", () => this.destroy());
    this.container.addEventListener("wheel", this.onPointerInput, { capture: true, passive: true });
    this.container.addEventListener("pointerdown", this.onPointerInput, CAPTURE);
    this.container.addEventListener("keydown", this.onKeyInput, CAPTURE);
    if (typeof ResizeObserver === "function") {
      this.observer = new ResizeObserver(this.onResizeObserved);
      this.observer.observe(this.container);
    }
  }
  diagram;
  options;
  canvas;
  eventBus;
  container;
  listeners = /* @__PURE__ */ new Set();
  subscriptions = [];
  observer;
  zoomScrollService;
  /** The last view the viewport set as its own (fit target, restore); user moves differ from it. */
  reference;
  /** The last view written, animation frames included. */
  lastWritten;
  /** The container size the current view was set or last resized for. */
  lastSize;
  writing = 0;
  userMoved = false;
  importing = false;
  imported = false;
  firstFitPending = true;
  /** Nothing has decided the view yet, so an empty first import shows 100 %. */
  emptyViewPending = true;
  /** The 100 % of an empty first import waits for the container to get a size (hidden tab). */
  emptyViewDue = false;
  /** `canFit()` of the current content; the zoom panel asks every frame, `contentBounds` may be costly. */
  fittable;
  lastZoom = 1;
  /** An automatic fit is due but the container had no size yet (hidden tab). */
  autoFitDue = false;
  /** A restored view waits for the container to get a size. */
  pendingView;
  /** What the fit that set the current view measured; cleared once anything else sets the view. */
  fitted;
  animation;
  resizeFrame;
  destroyed = false;
  zoom() {
    if (!this.destroyed) this.lastZoom = this.canvas.zoom();
    return this.lastZoom;
  }
  stepZoom(direction) {
    if (this.destroyed) return;
    this.beforeUserChange();
    const zoom = this.canvas.zoom();
    if (direction < 0 ? !canZoomOut(zoom) : !canZoomIn(zoom)) return;
    const zoomScroll = this.zoomScroll();
    if (zoomScroll) zoomScroll.stepZoom(direction);
    else this.canvas.zoom(stepOnRaster(zoom, direction));
  }
  zoomToActual() {
    if (this.destroyed) return;
    this.beforeUserChange();
    this.canvas.zoom(1);
  }
  focus() {
    if (!this.destroyed) this.canvas.focus?.();
  }
  fit(options) {
    if (this.destroyed) return;
    this.importing = false;
    this.fitTo(options?.animate !== false && !this.prefersReducedMotion());
  }
  canFit() {
    if (this.destroyed) return false;
    this.fittable ??= hasExtent(this.options.contentBounds());
    return this.fittable;
  }
  onChange(listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
  afterImport() {
    if (this.destroyed) return;
    this.importing = false;
    this.imported = true;
    this.fittable = void 0;
    if (this.firstFitPending && this.canFit()) {
      this.firstFitPending = false;
      this.emptyViewPending = false;
      this.emptyViewDue = false;
      if (!this.userMoved && !this.fitTo(false)) this.autoFitDue = true;
    } else if (this.firstFitPending && this.emptyViewPending) {
      this.emptyViewPending = false;
      if (!this.userMoved && !this.showActualSize()) this.emptyViewDue = true;
    }
    this.notify();
  }
  newDocument() {
    if (this.destroyed) return;
    this.cancelAnimation();
    this.fitted = void 0;
    this.userMoved = false;
    this.firstFitPending = true;
    this.emptyViewPending = true;
    this.emptyViewDue = false;
    this.autoFitDue = false;
    this.pendingView = void 0;
    this.fittable = void 0;
  }
  restore(viewbox) {
    if (this.destroyed) return;
    this.importing = false;
    this.imported = true;
    this.fittable = void 0;
    this.emptyViewPending = false;
    this.emptyViewDue = false;
    this.cancelAnimation();
    this.show(viewbox);
    this.notify();
  }
  freeRectCenter() {
    if (this.destroyed) return void 0;
    const centre = freeRectCenter(this.canvas.getSize(), this.options.insets());
    const view = this.currentView();
    if (!centre || !view) return void 0;
    return { x: view.x + centre.x / view.scale, y: view.y + centre.y / view.scale };
  }
  getViewbox() {
    if (this.destroyed) return void 0;
    if (this.pendingView) return { ...this.pendingView };
    const size = this.canvas.getSize();
    if (!hasSize(size)) return void 0;
    const { x, y, scale } = this.canvas.viewbox();
    return { x, y, width: size.width / scale, height: size.height / scale };
  }
  isAutomatic() {
    return !this.userMoved;
  }
  getState() {
    if (!this.imported) return void 0;
    const viewbox = this.getViewbox();
    return viewbox ? { viewbox, userMoved: this.userMoved } : void 0;
  }
  setState(state) {
    if (this.destroyed) return;
    this.importing = false;
    this.imported = true;
    this.fittable = void 0;
    this.cancelAnimation();
    this.userMoved = state.userMoved;
    this.autoFitDue = false;
    this.emptyViewPending = false;
    this.emptyViewDue = false;
    if (this.canFit()) this.firstFitPending = false;
    this.show(state.viewbox);
    this.notify();
  }
  /**
   * Fits again (jump) when `insets()` differs from what the fit that set the current view measured
   * on `side` and on no other side, while nothing else has set the view since and the content
   * bounds are still the ones it fitted: chrome that settles after a fit (the open legend's card
   * once its font has loaded) would leave the content off the free rectangle's centre, while any
   * other change of the chrome, and content the user created, moved or deleted since, keeps the view
   * (guide §5.9). A fit animation still running is retargeted instead. A container resize is left
   * to the resize refit. True when it fit again.
   */
  refitIfOnlyInsetChanged(side) {
    const fitted = this.fitted;
    if (this.destroyed || !fitted) return false;
    const size = this.canvas.getSize();
    if (size.width !== this.lastSize?.width || size.height !== this.lastSize.height) return false;
    const insets = this.options.insets();
    if (insets[side] === fitted.insets[side]) return false;
    if (INSET_SIDES.some((other) => other !== side && insets[other] !== fitted.insets[other]))
      return false;
    if (!sameBounds(this.options.contentBounds(), fitted.bounds)) return false;
    return this.fitTo(this.animation !== void 0);
  }
  /** `canvas.resized()`; viewbox changes it causes (keep-selection-visible) are not user moves. */
  resized() {
    if (this.destroyed) return;
    this.writing++;
    try {
      this.canvas.resized();
    } finally {
      this.writing--;
    }
  }
  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    this.cancelAnimation();
    if (this.resizeFrame !== void 0) cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = void 0;
    this.observer?.disconnect();
    this.container.removeEventListener("wheel", this.onPointerInput, CAPTURE);
    this.container.removeEventListener("pointerdown", this.onPointerInput, CAPTURE);
    this.container.removeEventListener("keydown", this.onKeyInput, CAPTURE);
    for (const [events, listener] of this.subscriptions.splice(0))
      this.eventBus.off(events, listener);
    this.listeners.clear();
  }
  /** Fits now (jump) or animated; false when there is nothing to fit or no container size. */
  fitTo(animate) {
    this.cancelAnimation();
    const bounds = this.options.contentBounds();
    if (!bounds) return false;
    const insets = this.options.insets();
    const target = fitViewbox(bounds, this.canvas.getSize(), insets);
    if (!target) return false;
    this.fitted = { bounds: { ...bounds }, insets: { ...insets } };
    this.userMoved = false;
    this.firstFitPending = false;
    this.autoFitDue = false;
    this.emptyViewPending = false;
    this.emptyViewDue = false;
    this.pendingView = void 0;
    const from = this.currentView();
    if (animate && from && !sameView(from, target) && typeof requestAnimationFrame === "function") {
      this.animate(from, target);
    } else {
      this.write(target);
    }
    return true;
  }
  animate(from, target) {
    this.reference = toView(target);
    const animation = { frame: 0 };
    const startedAt = performance.now();
    const step = (timestamp) => {
      if (this.animation !== animation) return;
      if (!hasSize(this.canvas.getSize())) {
        this.cancelAnimation();
        this.autoFitDue = true;
        return;
      }
      const progress = Math.min(1, Math.max(0, (timestamp - startedAt) / FIT_ANIMATION_MS));
      if (progress >= 1) {
        this.animation = void 0;
        this.write(target);
        return;
      }
      this.write(interpolateViewbox(from, target, easeStandard(progress)), false);
      animation.frame = requestAnimationFrame(step);
    };
    this.animation = animation;
    animation.frame = requestAnimationFrame(step);
  }
  cancelAnimation() {
    const animation = this.animation;
    if (!animation) return;
    this.animation = void 0;
    this.fitted = void 0;
    cancelAnimationFrame(animation.frame);
    if (this.lastWritten) this.reference = this.lastWritten;
  }
  /**
   * 100 % around the centre of the free rectangle, written as the viewport's own view; true when
   * the view is at 100 % afterwards, false while the container has no size.
   */
  showActualSize() {
    const size = this.canvas.getSize();
    const centre = freeRectCenter(size, this.options.insets());
    const view = this.currentView();
    if (!centre || !view) return false;
    if (Math.abs(view.scale - 1) <= VIEW_TOLERANCE) return true;
    this.cancelAnimation();
    this.write({
      x: view.x + centre.x / view.scale - centre.x,
      y: view.y + centre.y / view.scale - centre.y,
      width: size.width,
      height: size.height
    });
    return true;
  }
  /** Shows a restored view now, or once the container has a size. */
  show(viewbox) {
    this.fitted = void 0;
    if (hasSize(this.canvas.getSize())) {
      this.pendingView = void 0;
      this.write(viewbox);
    } else {
      this.pendingView = {
        x: viewbox.x,
        y: viewbox.y,
        width: viewbox.width,
        height: viewbox.height
      };
    }
  }
  /** Callers make sure the container has a size: diagram-js would set a scale of 0 otherwise. */
  write(box, asReference = true) {
    const size = this.canvas.getSize();
    const view = {
      x: box.x,
      y: box.y,
      scale: Math.min(size.width / box.width, size.height / box.height)
    };
    this.writing++;
    try {
      this.canvas.viewbox({ x: box.x, y: box.y, width: box.width, height: box.height });
    } finally {
      this.writing--;
    }
    this.lastWritten = view;
    if (asReference) this.reference = view;
    this.lastSize = { width: size.width, height: size.height };
  }
  currentView() {
    const size = this.canvas.getSize();
    const { x, y, scale } = this.canvas.viewbox();
    if (!(scale > 0 && Number.isFinite(scale))) return void 0;
    return { x, y, width: size.width / scale, height: size.height / scale, scale };
  }
  beforeUserChange() {
    this.importing = false;
    this.cancelAnimation();
  }
  zoomScroll() {
    if (this.zoomScrollService === void 0) {
      try {
        this.zoomScrollService = this.diagram.get("zoomScroll") ?? null;
      } catch {
        this.zoomScrollService = null;
      }
    }
    return this.zoomScrollService;
  }
  prefersReducedMotion() {
    if (this.options.prefersReducedMotion) return this.options.prefersReducedMotion();
    if (this.container.ownerDocument.documentElement.classList.contains(REDUCED_MOTION_CLASS))
      return true;
    return typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
  }
  isOwnView(current) {
    if (this.reference && sameView(current, this.reference)) return true;
    return this.animation !== void 0 && this.lastWritten !== void 0 && sameView(current, this.lastWritten);
  }
  // Not a flag around the own writes alone: with `canvas.deferUpdate` the event of an own change
  // arrives up to 300ms later, so it is recognised by its view.
  onViewboxChanged = (event) => {
    if (this.writing === 0 && !this.importing) {
      const current = event?.viewbox ?? this.canvas.viewbox();
      if (!this.isOwnView(current)) {
        this.cancelAnimation();
        this.fitted = void 0;
        this.userMoved = true;
        this.autoFitDue = false;
        this.emptyViewDue = false;
      }
    }
    this.notify();
  };
  onImportStart = () => {
    this.importing = true;
  };
  onImportDone = () => {
    this.importing = false;
    this.fittable = void 0;
    this.notify();
  };
  onContentChanged = () => {
    this.fittable = void 0;
  };
  // Guide §5.9: an element the user creates on the canvas never fits. Once such content exists,
  // only imports that keep the view follow, so the first automatic fit is no longer due.
  onElementsChanged = () => {
    this.fittable = void 0;
    if (this.imported && !this.importing && this.firstFitPending && this.canFit()) {
      this.firstFitPending = false;
    }
    this.notify();
  };
  // User input also ends an import window a failed import never closed with `import.done`.
  onPointerInput = () => {
    this.importing = false;
    this.cancelAnimation();
  };
  onKeyInput = (event) => {
    this.importing = false;
    if (matchChromeKey(event)) this.cancelAnimation();
  };
  onResizeObserved = () => {
    if (this.destroyed || this.resizeFrame !== void 0) return;
    if (typeof requestAnimationFrame !== "function") {
      this.onResize();
      return;
    }
    this.resizeFrame = requestAnimationFrame(() => {
      this.resizeFrame = void 0;
      this.onResize();
    });
  };
  onResize() {
    if (this.destroyed) return;
    const size = this.canvas.getSize();
    if (!hasSize(size)) return;
    const changed = this.lastSize?.width !== size.width || this.lastSize.height !== size.height;
    if (!changed && !this.pendingView && !this.autoFitDue && !this.emptyViewDue) return;
    this.lastSize = { width: size.width, height: size.height };
    this.resized();
    const restored = this.pendingView;
    if (restored) {
      this.pendingView = void 0;
      this.write(restored);
    }
    if (this.userMoved) return;
    if (this.emptyViewDue) {
      this.emptyViewDue = false;
      this.showActualSize();
      return;
    }
    if (this.autoFitDue || !restored && !this.firstFitPending) this.fitTo(false);
  }
  subscribe(events, listener) {
    this.eventBus.on(events, listener);
    this.subscriptions.push([events, listener]);
  }
  notify = () => {
    for (const listener of [...this.listeners]) listener();
  };
};
function toView(box) {
  return { x: box.x, y: box.y, scale: box.scale };
}
function hasSize(size) {
  return size.width > 0 && size.height > 0;
}
function hasExtent(bounds) {
  return bounds !== void 0 && (bounds.width > 0 || bounds.height > 0);
}
function sameBounds(bounds, other) {
  return bounds !== void 0 && bounds.x === other.x && bounds.y === other.y && bounds.width === other.width && bounds.height === other.height;
}
function stepOnRaster(zoom, direction) {
  const level = Math.round(Math.log10(zoom) / ZOOM_STEP) * ZOOM_STEP + ZOOM_STEP * direction;
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, 10 ** level));
}

// src/diagram-js/connect.ts
var HINT_SYNC_PRIORITY = 500;
function contentBoundsOf(options) {
  if (options.contentBounds) return () => options.contentBounds?.();
  const { modeler } = options;
  if (typeof modeler?.contentBounds === "function") return () => modeler.contentBounds?.();
  throw new Error(
    "@miragon/modeler-shell: connectDiagramJs needs the content bounds: pass options.contentBounds, or options.modeler with contentBounds() (@miragon/modeler-api 1.3)"
  );
}
function whenFontsSettle(fonts, settled) {
  let cancelled = false;
  let frame;
  const afterTwoFrames = (then) => {
    frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        frame = void 0;
        then();
      });
    });
  };
  const waitForFonts = () => {
    void fonts.ready.then(() => {
      if (cancelled) return;
      afterTwoFrames(() => {
        if (fonts.status === "loading") waitForFonts();
        else settled();
      });
    });
  };
  waitForFonts();
  return () => {
    cancelled = true;
    if (frame !== void 0) cancelAnimationFrame(frame);
  };
}
function refitWhenLegendSettles(shell, viewport) {
  const legend = shell.legend;
  const fonts = legend?.element.ownerDocument.fonts;
  if (!legend?.isOpen() || !fonts || typeof ResizeObserver !== "function" || typeof requestAnimationFrame !== "function") {
    return () => void 0;
  }
  let stopped = false;
  const observer = new ResizeObserver(() => {
    if (!legend.isOpen() || viewport.refitIfOnlyInsetChanged("left")) stop();
  });
  const unsubscribe = viewport.onChange(() => {
    if (!viewport.isAutomatic()) stop();
  });
  const cancelFontWait = whenFontsSettle(fonts, stop);
  function stop() {
    if (stopped) return;
    stopped = true;
    observer.disconnect();
    unsubscribe();
    cancelFontWait();
  }
  observer.observe(legend.element);
  return stop;
}
function connectDiagramJs(shell, diagram, options) {
  const service = shellServiceOf(diagram);
  const contentBounds = contentBoundsOf(options);
  const eventBus = diagram.get("eventBus");
  const prefersReducedMotion = options.prefersReducedMotion?.bind(options);
  const viewport = new DiagramViewport(diagram, {
    contentBounds,
    insets: () => shell.fitInsets(),
    ...prefersReducedMotion ? { prefersReducedMotion } : {}
  });
  shell.setZoomTarget(viewport);
  const detachPalette = service.attachShell(
    shell,
    options.canvasOwnsHistory === void 0 ? {} : { canvasOwnsHistory: options.canvasOwnsHistory }
  );
  viewport.resized();
  const isEmpty = options.isEmpty ? () => options.isEmpty?.() === true : () => !viewport.canFit();
  const syncHint = () => shell.setEmptyHint(isEmpty());
  eventBus.on(["import.done", "elements.changed"], HINT_SYNC_PRIORITY, syncHint);
  const stopLegendWatch = refitWhenLegendSettles(shell, viewport);
  let destroyed = false;
  const destroy = () => {
    if (destroyed) return;
    destroyed = true;
    eventBus.off(["import.done", "elements.changed"], syncHint);
    eventBus.off("diagram.destroy", destroy);
    stopLegendWatch();
    detachPalette();
    shell.setZoomTarget(null);
    viewport.destroy();
  };
  eventBus.on("diagram.destroy", destroy);
  return { viewport, destroy };
}

export {
  shellModule,
  createViewport,
  connectDiagramJs
};
//# sourceMappingURL=chunk-7GKXJQWI.js.map